import { useEffect, useState } from 'react';
import { currentEnvironment } from '@/shell/environment';

/**
 * Tells an open console that a newer one has been deployed (#303, part 2).
 *
 * WHY NOT THE SERVICE WORKER. `public/sw.js` caches nothing but the offline page, so it changes
 * only when that page does: a deploy of the app itself leaves it untouched and fires no update.
 * What a deploy always changes is the entry script's hashed name in `index.html`
 * (`/assets/index-<hash>.js`). So the check fetches `/` past every cache and compares that name
 * with the one this page was loaded from. An installed app can stay open for days, which is
 * exactly when this matters.
 */

const MODULE_SCRIPT = /<script[^>]*type="module"[^>]*src="([^"]+)"/;

/** The entry script's path in an `index.html`, or `null` when there is none. */
export function entryScriptOf(html: string): string | null {
  return MODULE_SCRIPT.exec(html)?.[1] ?? null;
}

export function loadedEntryScript(doc: Document = document): string | null {
  return doc.querySelector('script[type="module"][src]')?.getAttribute('src') ?? null;
}

/**
 * `true` only when the served `index.html` names a different entry script. Any failure (offline,
 * a 5xx, a page with no script) is `false`: no notice is better than a wrong one.
 */
export async function hasNewerVersion(
  loaded: string | null,
  fetcher: typeof fetch = fetch,
): Promise<boolean> {
  if (loaded === null) {
    return false;
  }
  try {
    const response = await fetcher('/', { cache: 'no-store' });
    if (!response.ok) {
      return false;
    }
    const served = entryScriptOf(await response.text());
    return served !== null && served !== loaded;
  } catch {
    return false;
  }
}

/** Every 5 minutes (decision 151: a release should show up soon), and each time the console comes back to the foreground. */
export const NEW_VERSION_CHECK_MS = 5 * 60 * 1000;

/**
 * `true` once a newer version is out; it stays `true`, since nothing un-deploys it. Deployed
 * builds only: the dev server and the e2e `test` build have no hashed entry to compare.
 */
export function useNewVersionAvailable(environment: string = currentEnvironment()): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if ((environment !== 'production' && environment !== 'staging') || available) {
      return;
    }
    const loaded = loadedEntryScript();
    let cancelled = false;

    function check() {
      void hasNewerVersion(loaded).then((newer) => {
        if (newer && !cancelled) {
          setAvailable(true);
        }
      });
    }

    function onVisible() {
      if (document.visibilityState === 'visible') {
        check();
      }
    }

    const timer = window.setInterval(check, NEW_VERSION_CHECK_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [environment, available]);

  return available;
}
